import React, { useState, useEffect } from "react";
import adminClasses from "../../../landing-pages/admin/admin.module.css";
import IdNameModel from "../../../../models/common/IdNameModel";
import modalUtils from "../../../../utils/modal-utils";
import toasterUtils from "../../../../utils/toaster-utils";
import loaderUtils from "../../../../utils/loader-utils";
import adminCompanyService from "../../../../services/admin/admin-company-service";
import CompanyConfigurationModel from "../../../../models/company/CompanyConfigurationModel";
import JobPostingComponent from "../../../company/job-posting/JobPostingComponent";
import designationService from "../../../../services/company/designation-service";
import { Autocomplete, Button, FormControl, InputLabel, MenuItem, Select, TextField } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import { GuidEnum } from "../../../../models/enums/GuidEnum";
import masterDataService from "../../../../services/master-data-service";

interface AdminPostRequirementProps {
    companiesList: IdNameModel[];
    companyConfiguration: CompanyConfigurationModel;
    jobTypes: IdNameModel[];
    assessmentTypes: IdNameModel[];
    primarySkills: IdNameModel[];
    experiences: IdNameModel[];
    cities: IdNameModel[];
    designations: IdNameModel[];
    salaries: IdNameModel[];
    escalationStatusList: IdNameModel[];
};

const AdminPostRequirementComponent = (props: AdminPostRequirementProps) => {
    const [recruiterList, setRecruiterList] = useState<IdNameModel[]>([]);
    const [companySearch, setCompanySearch] = useState<string>(GuidEnum.Empty);
    const [recruiterSearch, setRecruiterSearch] = useState<string>(GuidEnum.Empty);
    const [refreshRequirements, setRefreshRequirements] = useState<number>(2);
    const [designations, setDesignations] = useState<IdNameModel[]>([]);
    const [domains, setDomains] = useState<IdNameModel[]>([]);
    const [companyConfiguration, setCompanyConfiguration] = useState<CompanyConfigurationModel>(props.companyConfiguration);

    useEffect(() => {
        clearSearch();
    }, []);

    const clearSearch = () => {
        setCompanySearch(GuidEnum.Empty)
        setRecruiterSearch(GuidEnum.Empty);
        setRefreshRequirements(2);
    };

    const loadRecruiters = (companyId: string) => {
        setRefreshRequirements(2);
        setCompanySearch(companyId);
        let companyCofig: CompanyConfigurationModel = {
            companyId: companyId,
            countryId: 0
        };
        setCompanyConfiguration(companyCofig);
        if (companyId === "") {
            setRecruiterList([]);
            setDesignations([]);
            return;
        } else {
            modalUtils.showLoader();
            adminCompanyService.getRecruiterByCompanyforPostJob(companyId).then((res) => {
                modalUtils.closeModal();
                if (!res.isSuccess || res.errorMessages.length > 0) {
                    toasterUtils.showError(res.errorMessages.toString());
                    return;
                }
                setRecruiterSearch(GuidEnum.Empty);
                setRecruiterList(res.items);
                fillDesignations(companyId);
                fillDomains(companyId);
            }).catch((error) => {
                setRecruiterSearch(GuidEnum.Empty);
                modalUtils.closeModal();
                toasterUtils.showError(error);
            });
        }
    };

    const fillDesignations = (companyId: string) => {
        modalUtils.showLoader();
        designationService.fillDesignations(companyId).then((res) => {
            modalUtils.closeModal();
            if (!res.isSuccess || res.errorMessages.length > 0) {
                toasterUtils.showError(res.errorMessages.toString());
                return;
            }
            setDesignations(res.items);
        }).catch((error) => {
            modalUtils.closeModal();
            toasterUtils.showError(error);
        });
    };

    const fillDomains = (companyId: string) => {
        masterDataService.loadDomains(companyId).then((res) => {
            if (!res.isSuccess || res.errorMessages.length > 0) {
                toasterUtils.showError(res.errorMessages.toString());
                return;
            }
            setDomains(res.items);
        }).catch((error) => {
            toasterUtils.showError(error);
        });
    };

    const setRecruiter = (recruiterId: string) => {
        setRefreshRequirements(2);
        setRecruiterSearch(recruiterId);
    };

    const getRequirements = () => {
        if (companySearch === "" || recruiterSearch === "" || companySearch === GuidEnum.Empty || recruiterSearch === GuidEnum.Empty) {
            setRefreshRequirements(2);
        } else {
            setRefreshRequirements(refreshRequirements * 5);
        }
    };

    return (
        <div>
            <div className="card card-white">
                <div className="card-body">
                    <div className="row">
                        <div className="col-md-3">
                            <FormControl fullWidth margin="none" size="small">
                                <Autocomplete options={props.companiesList} value={props.companiesList.find((x) => x.guId === companySearch) || null}
                                    onChange={(_, newValue) => loadRecruiters(newValue ? newValue.guId : GuidEnum.Empty)} getOptionLabel={(option) => option?.name || ""} isOptionEqualToValue={(option, value) => option.guId === value.guId}
                                    renderInput={(params) => (
                                        <TextField {...params} label="Company" size="small" margin="normal" fullWidth required />
                                    )}
                                />
                            </FormControl>
                        </div>
                        <div className="col-md-3">
                            <FormControl fullWidth margin="none" size="small">
                                <Autocomplete options={recruiterList} value={recruiterList.find((x) => x.guId === recruiterSearch) || null}
                                    onChange={(_, newValue) => setRecruiter(newValue ? newValue.guId : GuidEnum.Empty)} getOptionLabel={(option) => option?.name || ""} isOptionEqualToValue={(option, value) => option.guId === value.guId}
                                    renderInput={(params) => (
                                        <TextField {...params} label="Recruiter" size="small" margin="normal" fullWidth required />
                                    )}
                                />
                            </FormControl>
                        </div>
                        <div className="col-md-3">
                            <Button variant="contained" color="success" onClick={() => getRequirements()} disabled={companySearch === "" || recruiterSearch === "" || companySearch === GuidEnum.Empty || recruiterSearch === GuidEnum.Empty} startIcon={<SearchIcon />} sx={{ ml: 2, mt: 2 }}>
                                Search
                            </Button>
                            <Button variant="contained" color="warning" onClick={() => clearSearch()} startIcon={<ClearIcon />} sx={{ ml: 2, mt: 2 }}>
                                Clear
                            </Button>
                        </div>
                    </div>
                </div>
            </div>
            {refreshRequirements > 2 && designations.length > 0 &&
                <JobPostingComponent
                    key={refreshRequirements}
                    companyConfiguration={companyConfiguration}
                    jobTypes={props.jobTypes}
                    assessmentTypes={props.assessmentTypes}
                    domains={domains}
                    primarySkills={props.primarySkills}
                    experiences={props.experiences}
                    cities={props.cities}
                    designations={designations}
                    salaries={props.salaries}
                    escalationStatusList={props.escalationStatusList}
                    profileId={recruiterSearch}
                    userId={recruiterSearch} />
            }
        </div>
    );
};
export default AdminPostRequirementComponent;